import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Calendar, TrendingUp, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDkk = (n) => `${Number(n || 0).toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr`;
const DK_MONTHS = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];

export default function StatementsTab({ token, shop }) {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [stmt, setStmt] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    axios.get(`${API}/pakkeshop/statement?year=${year}&month=${month}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setStmt(data))
      .catch(() => setStmt(null))
      .finally(() => setLoading(false));
  }, [year, month, token]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/pakkeshop/statement.pdf?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EMA-Opgoerelse-${shop?.shop_name?.replace(/[^a-z0-9]+/gi, "-") || "shop"}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloadet");
    } catch {
      toast.error("Kunne ikke hente opgørelse");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="statements-panel">
      {/* Period picker */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex items-center gap-3 flex-wrap">
        <Calendar size={16} className="text-[#475569]" />
        <div className="text-sm font-semibold text-[#0A1128]">Periode:</div>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" data-testid="stmt-month">
          {DK_MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" data-testid="stmt-year">
          {[now.getFullYear() - 1, now.getFullYear()].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex-1" />
        <button onClick={downloadPdf} disabled={downloading || !stmt} className="btn-primary text-sm" data-testid="stmt-download">
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <><Download size={14} /> Download PDF</>}
        </button>
      </div>

      {loading && <div className="text-[#475569] text-center py-10">Indlæser opgørelse...</div>}

      {!loading && stmt && (
        <>
          {/* Big total card */}
          <div className="bg-[#0A1128] text-white rounded-xl p-7 border border-brand-red">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Total til udbetaling</div>
            <div className="font-display font-black text-5xl text-white mt-3 tracking-tighter" data-testid="stmt-grand-total">
              {fmtDkk(stmt.grand_total)}
            </div>
            <div className="text-xs text-white/60 mt-1">{stmt.month_label}</div>
          </div>

          {/* 3-line breakdown */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <div className="p-5 border-b border-[#E2E8F0]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Sammensætning</div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-[#E2E8F0]" data-testid="stmt-row-received">
                  <td className="p-4">Modtagne pakker</td>
                  <td className="p-4 text-right text-[#475569]">{stmt.received_count} stk × {fmtDkk(stmt.rate_received)}</td>
                  <td className="p-4 text-right font-semibold text-[#0A1128]">{fmtDkk(stmt.received_total)}</td>
                </tr>
                <tr className="border-b border-[#E2E8F0]" data-testid="stmt-row-handover">
                  <td className="p-4">Udleverede pakker</td>
                  <td className="p-4 text-right text-[#475569]">{stmt.handed_over_count} stk × {fmtDkk(stmt.rate_handover)}</td>
                  <td className="p-4 text-right font-semibold text-[#0A1128]">{fmtDkk(stmt.handover_total)}</td>
                </tr>
                <tr data-testid="stmt-row-return">
                  <td className="p-4">Retur-håndteringer</td>
                  <td className="p-4 text-right text-[#475569]">{stmt.returned_count} stk × {fmtDkk(stmt.rate_return)}</td>
                  <td className="p-4 text-right font-semibold text-[#0A1128]">{fmtDkk(stmt.return_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-xs text-[#475569] text-center">
            Opgørelser låses 1. i måneden efter perioden og udbetales senest 5 hverdage efter. PDF-opgørelsen indeholder
            specifikation pr. pakke.
          </p>
        </>
      )}
    </div>
  );
}
